import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import deleteTokens from '@amzn/meridian-tokens/base/icon/trash';
import Button from '@amzn/meridian/button';
import DatePicker from '@amzn/meridian/date-picker';
import Icon from '@amzn/meridian/icon';
import InputGroup from '@amzn/meridian/input-group';
import Row from '@amzn/meridian/row';
import TimePicker from '@amzn/meridian/time-picker';
import Tooltip from '@amzn/meridian/tooltip';

import { isNil } from 'lodash';

const TIMESTAMP_REGEXP = /^\d\d\d\d-\d\d-\d\d \d\d:\d\d:\d\d$/;
const DATE_REGEXP = /^\d\d\d\d-\d\d-\d\d$/;
const TIME_REGEXP = /^\d\d:\d\d:\d\d$/;

interface ITimestampPicker {
    readonly timestamp: string | undefined;
    readonly setTimestamp: Dispatch<SetStateAction<string | undefined>>;
}

export const TimestampPicker: React.FC<ITimestampPicker> = (props) => {
    const { timestamp, setTimestamp } = props;

    const originalDate = timestamp && timestamp.split(' ')[0];
    const originalTime = timestamp && timestamp.split(' ')[1];
    const [rawDate, setRawDate] = useState<string | undefined>(originalDate);
    const [rawTime, setRawTime] = useState<string | undefined>(originalTime);

    useEffect(() => {
        if (isNil(timestamp)) {
            setRawDate(undefined);
            setRawTime(undefined);
        } else if (timestamp.match(TIMESTAMP_REGEXP)) {
            setRawDate(timestamp.split(' ')[0]);
            setRawTime(timestamp.split(' ')[1]);
        }
    }, [timestamp]);

    useEffect(() => {
        if (isNil(rawDate) || !rawDate.match(DATE_REGEXP)) {
            // do nothing
        } else if (isNil(rawTime)) {
            setTimestamp(rawDate + ' 00:00:00');
        } else if (!rawTime.match(TIME_REGEXP)) {
            // do nothing
        } else {
            setTimestamp(rawDate + ' ' + rawTime);
        }
    }, [rawDate, rawTime]);

    const clearTimestamp = () => {
        setTimestamp(undefined);
        setRawDate(undefined);
        setRawTime(undefined);
    };

    return (
        <Row widths={['fit', 'fill']}>
            <Tooltip position={'end'} title={'Clear timestamp'}>
                <Button onClick={clearTimestamp} type={'tertiary'} aria-label={`clear-timestamp`}>
                    <Icon tokens={deleteTokens} />
                </Button>
            </Tooltip>
            <InputGroup suffix={'UTC'}>
                <DatePicker value={rawDate} onChange={setRawDate} placeholder={'Enter date...'} />
                <TimePicker
                    value={rawTime}
                    format={{ hour: '2-digit', minute: '2-digit', second: '2-digit' }}
                    onChange={setRawTime}
                    placeholder={'Enter time...'}
                />
            </InputGroup>
        </Row>
    );
};

export function formatDate(date: Date): string {
    const datePart = [
        date.getUTCFullYear().toString().padStart(4, '0'),
        (date.getUTCMonth() + 1).toString().padStart(2, '0'),
        date.getUTCDate().toString().padStart(2, '0')
    ].join('-');
    const timePart = [
        date.getUTCHours().toString().padStart(2, '0'),
        date.getUTCMinutes().toString().padStart(2, '0'),
        date.getUTCSeconds().toString().padStart(2, '0')
    ].join(':');

    return `${datePart} ${timePart}`;
}
